import SFetch from '@app/utils/sfetch'

export default class BudgetTable {
  element: HTMLElement
  lockedPeriods: string[]

  constructor(element: HTMLElement) {
    this.element = element
    this.lockedPeriods = [
      ...document.querySelectorAll('[data-behaviour="budget-lock"]'),
    ].reduce((result, el) => {
      const element = el as HTMLElement
      if (element.dataset.locked === 'true' && element.dataset.period) {
        result.push(element.dataset.period)
      }

      return result
    }, [] as string[])

    this.initListenerOnLedgerSelect()
    this.initListenerOnIndividualCells()
    this.initLock()
    this.initCalculateTotals()
    this.calculateTotals()
    this.initTooltipOnLargeAccountNames()
  }

  initListenerOnLedgerSelect(): void {
    const ledgerSelect = document.getElementById('budget-ledger-dropdown')

    if (ledgerSelect) {
      ledgerSelect.addEventListener(
        'change',
        (el) => void this.changeLedger(el.target as HTMLSelectElement)
      )
    }
  }

  async changeLedger(target: HTMLSelectElement): Promise<void> {
    if (!target.value) {
      return
    }

    type ResponseRow = {
      elementId: string
      budgetType: string
      value: string
      url: string
    }

    try {
      const response = await SFetch.json<{
        rows: ResponseRow[]
        divider: string
      }>(target.value, { method: 'POST' })

      if (!response) {
        return
      }

      const selectedPeriod: HTMLOptionElement | null =
        target.selectedOptions.item(0)

      if (selectedPeriod) {
        const selectedPeriodValue =
          selectedPeriod.attributes.getNamedItem('period')?.value
        const selectedPeriodText = selectedPeriod.text
        const selectedLedgerId = selectedPeriod.dataset.ledgerId

        ;(
          document.querySelector(
            '[data-behaviour="budget-origin-period"]'
          ) as HTMLInputElement
        ).value = selectedPeriodValue || ''
        ;(
          document.getElementById('actuals-column-header') as HTMLElement
        ).innerText = `${selectedPeriodText} ${window.I18n.t(
          'js.report_period.relative.YTD'
        )}`
        ;(
          document.querySelector(
            '[data-behaviour="budget-divider"]'
          ) as HTMLInputElement
        ).value = response?.divider

        response.rows.forEach((row) => {
          let cell

          if (row.budgetType === 'account') {
            const accountRow = document.querySelector(
              `[data-budget-type="account"][data-element-id="${row.elementId}"]`
            )
            if (accountRow) {
              cell = accountRow.children[1].children[0]
              cell.textContent = row.value
              ;(cell as HTMLElement).dataset.url = row.url
              ;(cell as HTMLElement).dataset.value = row.value
              if (selectedLedgerId) {
                ;(cell as HTMLElement).dataset.ledgerId = selectedLedgerId
              }
            }
          } else {
            const accountRow = document.querySelector(
              `[data-budget-type="group"][data-element-id="${row.elementId}"]`
            )
            if (accountRow) {
              cell = accountRow.children[1]
              cell.textContent = row.value
            }
          }
        })
      }
    } catch (e) {
      console.log(e)
    }
  }

  initTooltipOnLargeAccountNames(): void {
    const accountNameCells = [
      ...document.querySelectorAll('tr td:first-child'),
    ] as HTMLElement[]

    accountNameCells.map((el) => {
      // If the scroll width is larger than the element width it means ellipsis were places through the css
      if (el.offsetWidth < el.scrollWidth) {
        el.dataset.toggle = 'tooltip'
        el.dataset.placement = 'bottom'
        el.title = el.innerText
      }
    })
  }

  initListenerOnIndividualCells(): void {
    this.element.addEventListener('click', (event) => {
      const budgetInput = (event.target as Element).closest(
        '[data-behaviour="budget-input"]'
      )
      if (budgetInput) {
        budgetInput.closest('td')?.querySelector('input')?.focus()
      }
    })

    this.element.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' &&
        target.closest('[data-behaviour="budget-input"]')
      ) {
        ;(target as HTMLInputElement).select()
      }
    })

    this.element.addEventListener('change', (event) => {
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' &&
        target.closest('[data-behaviour="budget-input"]')
      ) {
        target.classList.add('budget-changed')
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.submitValues()
      }
    })
  }

  initLock(): void {
    this.click('[data-behaviour="budget-lock"]', async (event: Event) => {
      event.preventDefault()

      if (!(event.target instanceof Element)) return
      const target = event.target.closest<HTMLAnchorElement>('a')
      if (!target) return

      let locked = false
      const clickedPeriod = target.dataset.period ?? ''

      if (this.lockedPeriods.includes(clickedPeriod)) {
        this.lockedPeriods.splice(this.lockedPeriods.indexOf(clickedPeriod), 1)
      } else {
        this.lockedPeriods.push(clickedPeriod)
        locked = true
      }

      const href = target.getAttribute('href')
      if (href) {
        await SFetch.raw(href, { method: 'POST' })

        const header = target.closest('th')
        if (!header) return

        const colIndex =
          Array.from(header.parentElement!.children).indexOf(header) + 1

        document
          .querySelectorAll(
            `[data-behaviour~='budget-table'] tr td:nth-child(${colIndex}) input`
          )
          .forEach((el) => {
            ;(el as HTMLInputElement).disabled = locked
          })

        document
          .querySelectorAll(
            `[data-behaviour~='budget-table'] tr td:nth-child(${colIndex})`
          )
          .forEach((el) => {
            el.classList.toggle('budget-locked')
          })

        document
          .querySelectorAll(
            `[data-behaviour~='budget-table'] th [data-period~='${clickedPeriod}']`
          )
          .forEach((el) => {
            el.closest('th')?.classList.toggle('budget-locked')
            const icon = el.querySelector('i')
            if (icon) {
              icon.classList.toggle('fa-unlock')
              icon.classList.toggle('fa-lock')
              icon.classList.toggle('muted')
            }
          })
      }
    })
  }

  initCalculateTotals(): void {
    document.addEventListener('calculate-budget-totals', () => {
      this.calculateTotals()
    })
  }

  calculateTotals(): void {
    if (document.querySelectorAll('.total-budget').length === 0) {
      return
    }

    ;[...document.querySelectorAll('tr[data-budget-type="account"]')].forEach(
      (element) => {
        this.updateTotal(element as HTMLElement)
      }
    )
  }

  updateTotal(row: HTMLElement): void {
    if (row.querySelectorAll('.total-budget').length === 0) return

    let totalValue = 0.0
    const rowInputs: Element[] = [...row.querySelectorAll('.budget-input')]

    rowInputs.forEach((inputElement) => {
      const centValue = (inputElement as HTMLInputElement).value.replace(
        /[^0-9-]/g,
        ''
      )
      const inputValue = parseFloat(centValue) / 100
      if (!isNaN(inputValue)) {
        totalValue += inputValue
      }
    })

    const budgetTotal = row.querySelector('.total-budget')
    if (budgetTotal) {
      budgetTotal.textContent = window.I18n.toNumber(totalValue, {
        precision: 2,
      })
    }
  }

  async submitValues(): Promise<void> {
    const changedFields = Array.from(
      this.element.querySelectorAll<HTMLInputElement>(
        'input.budget-changed:not(.budget-submitted)'
      )
    )

    const uniqRowsAffected = [
      ...new Set(
        changedFields
          .map((el) => el.closest('tr'))
          .filter((row): row is HTMLTableRowElement => row !== null)
      ),
    ]

    const params = new URLSearchParams()
    changedFields.forEach((el) => {
      if (el.name) params.append(el.name, el.value)
    })

    changedFields.forEach((el) => el.classList.add('budget-submitted'))
    const action = this.element.closest('form')?.getAttribute('action')

    if (action) {
      type UpdateEntry = {
        element_id: string
        index: number
        value_cents: number
      }

      try {
        const response = await SFetch.raw(action, {
          method: 'POST',
          body: params,
        })
        const data = (await response.json()) as { entries: UpdateEntry[] }

        data.entries.forEach(
          ({ element_id: elementId, index, value_cents: valueCents }) => {
            const row = this.element.querySelector(
              `tr[data-element-id="${elementId}"][data-budget-type="account"]`
            )
            if (!row) return
            const input = row.querySelectorAll<HTMLInputElement>(
              'td[data-behaviour="budget-input"] input'
            )[index]
            if (input) input.value = String(valueCents)
          }
        )

        changedFields.forEach((el) => {
          el.classList.remove('budget-changed', 'budget-submitted')
          this.localizeBudgetValue(el)
        })

        uniqRowsAffected.forEach((row) => {
          this.updateTotal(row)
        })
      } catch (e) {
        console.log(e)
      }
    }
  }

  localizeBudgetValue(element: HTMLInputElement): void {
    if (element.value) {
      const elementValue = parseInt(element.value.replace(/[^0-9-]/g, '')) / 100
      element.value = window.I18n.toNumber(elementValue, { precision: 2 })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  click<ClickFunction extends Function>(
    selector: string,
    f: ClickFunction
  ): void {
    // do an explicit find again to find the frozen headers
    document
      .querySelectorAll('[data-behaviour="budget-table"]')
      .forEach((table) => {
        table.addEventListener('click', (event) => {
          if ((event.target as Element).closest(selector)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            f(event)
          }
        })
      })
  }
}
